import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Leaf, ArrowLeft, Sprout, Utensils, Users, Check,
  ChevronRight, Award, Target, TreeDeciduous
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const phases = [
  {
    id: "Phase_1",
    name: "GROW",
    icon: Sprout,
    description: "Select 1-2 crops and start your garden",
    color: "emerald",
    tasks: ["Choose your growing space", "Select starter crops", "Set up your garden", "Follow weekly digital advisories"]
  },
  {
    id: "Phase_2",
    name: "COOK",
    icon: Utensils,
    description: "Prepare what you've harvested",
    color: "orange",
    tasks: ["Harvest your first crop", "Find recipes in the vault", "Document your meal", "Share your legacy moment"]
  },
  {
    id: "Phase_3",
    name: "TEACH",
    icon: Users,
    description: "Pass your knowledge to a neighbor",
    color: "purple",
    tasks: ["Connect with a neighbor", "Share seeds or knowledge", "Help them start their garden", "Become a Legacy Farmer"]
  }
];

const growingSpaces = [
  { id: "full_garden", name: "Full Garden", description: "Backyard or large plot" },
  { id: "container", name: "Container Garden", description: "Pots on patio or balcony" },
  { id: "indoor_windowsill", name: "Indoor Windowsill", description: "Herbs and small plants" },
  { id: "community_plot", name: "Community Plot", description: "Shared garden space" }
];

export default function CommunityHarvest() {
  const { user } = useAuth();
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [showEnrollForm, setShowEnrollForm] = useState(false);

  useEffect(() => {
    if (user) fetchStatus();
    else setLoading(false);
  }, [user]);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/food/seed-to-table/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setStatus(await res.json());
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    } finally {
      setLoading(false);
    }
  };

  const enroll = async () => {
    if (!selectedSpace) {
      toast.error('Please select your growing space');
      return;
    }

    setEnrolling(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/food/seed-to-table/enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          growing_space: selectedSpace,
          crops_selected: []
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchStatus();
        setShowEnrollForm(false);
      }
    } catch (err) {
      toast.error('Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  const completePhase = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/food/seed-to-table/complete-phase`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.badges_earned?.length) {
          toast.success(`Badge earned: ${data.badges_earned.join(', ')}`);
        }
        fetchStatus();
      }
    } catch (err) {
      toast.error('Failed to complete phase');
    }
  };

  const getCurrentPhaseIndex = () => {
    if (!status?.enrolled) return -1;
    return phases.findIndex(p => p.id === status.current_phase);
  };

  const phaseIndex = getCurrentPhaseIndex();

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-emerald-50/30" data-testid="community-harvest">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-700 via-green-700 to-emerald-800 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/food" className="inline-flex items-center text-emerald-200 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to LegacyTable
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <Leaf className="h-8 w-8" />
            <h1 className="text-3xl font-bold">Community Harvest</h1>
          </div>
          <p className="text-emerald-100">Urban gardening support, micro-grants, and the Seed-to-Table cycle</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Seed-to-Table Progress */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-stone-800 mb-6">Seed-to-Table Cycle</h2>
          
          {!user ? (
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="py-12 text-center">
                <Sprout className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-stone-800 mb-2">Start Your Food Sovereignty Journey</h3>
                <p className="text-stone-600 mb-6">Log in to enroll in the Seed-to-Table program</p>
                <Link to="/login">
                  <Button className="bg-emerald-600 hover:bg-emerald-700">Log In</Button>
                </Link>
              </CardContent>
            </Card>
          ) : !status?.enrolled ? (
            <Card>
              <CardContent className="py-8">
                {!showEnrollForm ? (
                  <div className="text-center">
                    <Sprout className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-stone-800 mb-2">Begin Your Journey</h3>
                    <p className="text-stone-600 mb-6 max-w-md mx-auto">
                      The Seed-to-Table cycle is a 3-phase pathway to food sovereignty. 
                      Grow your own food, cook from your harvest, and teach your community.
                    </p>
                    <Button 
                      onClick={() => setShowEnrollForm(true)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Sprout className="h-4 w-4 mr-2" /> Enroll Now
                    </Button>
                  </div>
                ) : (
                  <div className="max-w-lg mx-auto">
                    <h3 className="text-lg font-bold text-stone-800 mb-4">Select Your Growing Space</h3>
                    <div className="space-y-3 mb-6">
                      {growingSpaces.map(space => (
                        <div
                          key={space.id}
                          onClick={() => setSelectedSpace(space.id)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedSpace === space.id 
                              ? 'border-emerald-500 bg-emerald-50' 
                              : 'border-stone-200 hover:border-stone-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-stone-800">{space.name}</p>
                              <p className="text-sm text-stone-500">{space.description}</p>
                            </div>
                            {selectedSpace === space.id && (
                              <Check className="h-5 w-5 text-emerald-500" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-3">
                      <Button 
                        onClick={enroll}
                        disabled={enrolling || !selectedSpace}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      >
                        {enrolling ? "Enrolling..." : "Start Phase 1"}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setShowEnrollForm(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : status.current_phase === "Graduated" ? (
            <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
              <CardContent className="py-12 text-center">
                <Award className="h-16 w-16 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">Congratulations, Legacy Farmer!</h3>
                <p className="text-amber-100 mb-6 max-w-md mx-auto">
                  You've completed the full Seed-to-Table cycle. You're now eligible to lead a Food Guild.
                </p>
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold">{status.self_sufficiency_score}%</div>
                    <p className="text-amber-100 text-sm">Self-Sufficiency Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Progress Bar */}
              <Card className="mb-6">
                <CardContent className="py-6">
                  <div className="flex items-center justify-between mb-4">
                    {phases.map((phase, idx) => {
                      const isComplete = idx < phaseIndex || status.milestones?.[phase.id]?.completed;
                      const isCurrent = idx === phaseIndex;
                      
                      return (
                        <div key={phase.id} className="flex items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isComplete ? 'bg-emerald-500 text-white' :
                            isCurrent ? 'bg-emerald-100 text-emerald-600 border-2 border-emerald-500' :
                            'bg-stone-100 text-stone-400'
                          }`}>
                            {isComplete ? <Check className="h-5 w-5" /> : <phase.icon className="h-5 w-5" />}
                          </div>
                          <div className="ml-3">
                            <p className={`font-medium ${isCurrent ? 'text-emerald-600' : 'text-stone-800'}`}>
                              {phase.name}
                            </p>
                            <p className="text-xs text-stone-500">{phase.description}</p>
                          </div>
                          {idx < phases.length - 1 && (
                            <ChevronRight className="h-6 w-6 mx-4 text-stone-300" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Progress value={((phaseIndex + 1) / phases.length) * 100} className="h-2" />
                </CardContent>
              </Card>

              {/* Current Phase Detail */}
              {phaseIndex >= 0 && phaseIndex < phases.length && (
                <Card className={`border-2 border-${phases[phaseIndex].color}-200`}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl bg-${phases[phaseIndex].color}-100`}>
                        {(() => {
                          const Icon = phases[phaseIndex].icon;
                          return <Icon className={`h-6 w-6 text-${phases[phaseIndex].color}-600`} />;
                        })()}
                      </div>
                      <div>
                        <Badge className={`bg-${phases[phaseIndex].color}-100 text-${phases[phaseIndex].color}-700 mb-1`}>
                          Current Phase
                        </Badge>
                        <CardTitle>Phase {phaseIndex + 1}: {phases[phaseIndex].name}</CardTitle>
                        <CardDescription>{phases[phaseIndex].description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-6">
                      <p className="text-sm font-medium text-stone-700 mb-3">Your Setup:</p>
                      <div className="flex gap-4">
                        <Badge variant="outline">
                          {status.growing_space?.replace('_', ' ')}
                        </Badge>
                        {status.crops?.map(crop => (
                          <Badge key={crop} className="bg-emerald-100 text-emerald-700">
                            {crop}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <p className="text-sm font-medium text-stone-700 mb-3">Tasks to Complete:</p>
                    <div className="space-y-2 mb-6">
                      {phases[phaseIndex].tasks.map((task, idx) => (
                        <div key={`item-${idx}`} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                          <div className="w-6 h-6 rounded-full border-2 border-stone-300" />
                          <span className="text-stone-700">{task}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Button 
                      onClick={completePhase}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Check className="h-4 w-4 mr-2" /> Mark Phase Complete
                    </Button>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </section>

        {/* Resources Grid */}
        <section>
          <h2 className="text-2xl font-bold text-stone-800 mb-6">Community Resources</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="py-6">
                <Target className="h-10 w-10 text-emerald-500 mb-4" />
                <h3 className="font-bold text-stone-800 mb-2">Micro-Grants</h3>
                <p className="text-sm text-stone-600 mb-4">
                  Apply for seed money and garden supplies for your community plot.
                </p>
                <Button variant="outline" className="w-full" disabled>
                  Coming Soon
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="py-6">
                <Leaf className="h-10 w-10 text-emerald-500 mb-4" />
                <h3 className="font-bold text-stone-800 mb-2">Agri-Tech Partners</h3>
                <p className="text-sm text-stone-600 mb-4">
                  Connect with local urban farming organizations and resources.
                </p>
                <Button variant="outline" className="w-full" disabled>
                  Coming Soon
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="py-6">
                <Users className="h-10 w-10 text-emerald-500 mb-4" />
                <h3 className="font-bold text-stone-800 mb-2">Food Guilds</h3>
                <p className="text-sm text-stone-600 mb-4">
                  Join a regional community of growers sharing knowledge.
                </p>
                <Link to="/food/guilds">
                  <Button variant="outline" className="w-full">
                    Browse Guilds
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
}
